import { useMemo } from 'react'

import type { Appointment } from '~/api/graphql/generated/types'
import { useAppointmentsWidgetSubscription } from '~/api/graphql/subscriptions/appointments'

import { useSubscriptionRetry } from './useSubscriptionRetry'

export const AppointmentsTab = {
  PREVIOUS_TAB_ID: 0,
  SCHEDULED_TAB_ID: 1,
} as const

export type AppointmentsTabType =
  | typeof AppointmentsTab.PREVIOUS_TAB_ID
  | typeof AppointmentsTab.SCHEDULED_TAB_ID

/**
 * Custom hook for subscribing to appointment data changes for a specific patient
 * Automatically handles subscription retries for network errors and returns
 * appointments categorized by previous (past) and scheduled (future)
 *
 * @param patientId - The ID of the patient to subscribe to appointment updates for
 * @param offset - Pagination offset for appointments list
 * @param limit - Maximum number of appointments to fetch per page
 * @param selectedTabId - The currently selected tab id to fetch data for (0 = previous, 1 = scheduled)
 * @returns Object containing previous and scheduled appointments, counters, loading state, and error state
 */
export const useAppointmentsSubscription = (
  patientId: string,
  offset: number = 0,
  limit: number = 10,
  selectedTabId?: AppointmentsTabType,
): {
  error: any
  loading: boolean
  previousAppointments: Appointment[]
  previousAppointmentsCount: number
  scheduledAppointments: Appointment[]
  scheduledAppointmentsCount: number
} => {
  const { data, loading, error, restart } = useAppointmentsWidgetSubscription({
    patientId,
    offset,
    limit,
    includePrevious: selectedTabId === AppointmentsTab.PREVIOUS_TAB_ID,
    includeScheduled: selectedTabId === AppointmentsTab.SCHEDULED_TAB_ID,
  })

  const transformedData = useMemo(() => {
    if (!data?.appointmentWidget) {
      return {
        previousAppointments: [],
        scheduledAppointments: [],
        previousAppointmentsCount: 0,
        scheduledAppointmentsCount: 0,
      }
    }

    const widget = data.appointmentWidget
    const counters = widget.appointmentCounters

    return {
      previousAppointments: widget.previous || [],
      scheduledAppointments: widget.scheduled || [],
      previousAppointmentsCount: counters?.previous ?? 0,
      scheduledAppointmentsCount: counters?.scheduled ?? 0,
    }
  }, [data])

  useSubscriptionRetry({
    error,
    restart,
    subscriptionName: 'Appointments subscription',
  })

  return {
    previousAppointments: transformedData.previousAppointments,
    previousAppointmentsCount: transformedData.previousAppointmentsCount,
    scheduledAppointments: transformedData.scheduledAppointments,
    scheduledAppointmentsCount: transformedData.scheduledAppointmentsCount,
    loading,
    error,
  }
}
